// Asset locators (return-knowledge-images-across-surfaces) — a discriminated
// union on `kind`, mirroring `backend/src/knowledge_assets/contracts.py`'s
// `AssetLocator` union. `kind` carries a Pydantic default on every member
// (like `type` on every SSE event), so it is NEVER in that member's
// `required` list — `.default()` here reproduces that, not `.optional()`
// alone, which would leave `kind` absent from the union's discriminant.
//
// Extracted to its own zero-dependency module (PR 4,
// `return-knowledge-images-across-surfaces`) so both `chat-event-schemas.ts`
// (chat response assets) and `reports.ts` (report image relations, schema
// v2) can depend on it without creating a cycle: `chat-event-schemas.ts`
// already imports `reportRefSchema` from `./reports`, so `reports.ts`
// cannot import a locator type back out of `chat-event-schemas.ts`.
// `chat-event-schemas.ts` re-exports everything here (`export * from
// "./asset-locator"`) so its existing consumers are unaffected.

import { z } from "zod";

export const wholeImageLocatorSchema = z
  .object({
    kind: z.literal("whole_image").default("whole_image"),
    attachment_id: z.guid().nullable().optional(),
    origin: z.enum(["source", "chat_attachment"]).default("source"),
  })
  .strict();

export const notionBlockLocatorSchema = z
  .object({
    kind: z.literal("notion_block").default("notion_block"),
    block_id: z.string().min(1).max(128),
    position: z.number().int().min(1),
  })
  .strict();

export const documentPageLocatorSchema = z
  .object({
    kind: z.literal("document_page").default("document_page"),
    container_format: z.enum(["pdf", "pptx"]),
    position: z.number().int().min(1),
  })
  .strict();

export const documentImageLocatorSchema = z
  .object({
    kind: z.literal("document_image").default("document_image"),
    container_format: z.enum(["pdf", "docx", "pptx", "xlsx"]),
    position_kind: z.enum(["page", "document", "slide", "sheet"]),
    position: z.number().int().min(1),
    image_ordinal: z.number().int().min(1),
  })
  .strict();

export const videoFrameLocatorSchema = z
  .object({
    kind: z.literal("video_frame").default("video_frame"),
    timestamp_ms: z.number().int().min(0),
  })
  .strict();

export const assetLocatorSchema = z.discriminatedUnion("kind", [
  wholeImageLocatorSchema,
  notionBlockLocatorSchema,
  documentPageLocatorSchema,
  documentImageLocatorSchema,
  videoFrameLocatorSchema,
]);

export type AssetLocator = z.infer<typeof assetLocatorSchema>;
